import { ConsensusRegisterCollection, ReadPolicy } from '@fluidframework/register-collection';
import { mfsError } from '../../errors';
import { logVerbose } from '../../log';
import { LabelTracker, RESET_LABEL_ID } from '../../store/labels';
import { MfsItemId } from '../../store/mfsTypes';

/** Generates the key to be used with operations of the LabelTracker */
const getLabelKey = (label: string, labelId: string): string => `${label}+-${labelId}`;

/** Fluid implementation of the item label tracker to track label + labelIds that are unique in a Pod context */
export class MfsFluidLabelTracker implements LabelTracker {
  constructor(private consensusRegistry: ConsensusRegisterCollection<MfsItemId>) {}

  /** @inheritdoc */
  async track(label: string, labelId: string, itemId: MfsItemId): Promise<void> {
    logVerbose('track', `claiming label, labelId for itemId: ${itemId}`);

    const wasAcked = await this.consensusRegistry.write(getLabelKey(label, labelId), itemId);

    logVerbose('track', `claimed: ${wasAcked} label, labelId for itemId: ${itemId}`);

    if (!wasAcked) {
      throw mfsError.consensusError('claim', label, labelId, itemId, [...this.consensusRegistry.keys()]);
    }
  }

  /** @inheritdoc */
  async untrack(label: string, labelId: string): Promise<void> {
    const itemId = this.getItemId(label, labelId);

    if (!itemId) {
      logVerbose('untrack', `label, labelId are already not in use.`);
      return;
    }

    // ConsensusRegisterCollection does not support delete, so we set the value to RESET_LABEL_ID instead to indicate deletion.
    logVerbose('untrack', `releasing labelId used by itemId: ${itemId}`);

    const wasAcked = await this.consensusRegistry.write(getLabelKey(label, labelId), RESET_LABEL_ID);

    logVerbose('untrack', `released:${wasAcked} labelId used by itemId: ${itemId}`);

    if (!wasAcked) {
      throw mfsError.consensusError('release', label, labelId, itemId, [...this.consensusRegistry.keys()]);
    }
  }

  /** @inheritdoc */
  getItemId(label: string, labelId: string): MfsItemId | undefined {
    const itemId = this.consensusRegistry.read(getLabelKey(label, labelId), ReadPolicy.Atomic);
    logVerbose('getItemId', `Read atomic itemId: ${itemId ?? 'UNDEFINED'}`);
    return itemId ? itemId : undefined;
  }

  /** @inheritdoc */
  async cleanUp(): Promise<void> {
    this.consensusRegistry.removeAllListeners();
    const deletions: Promise<boolean>[] = [];
    this.consensusRegistry.keys().forEach((labelKey) => {
      // ConsensusRegisterCollection does not support delete - we set the value to an invalid id instead to indicate deletion
      deletions.push(this.consensusRegistry.write(labelKey, RESET_LABEL_ID));
    });

    await Promise.all(deletions);

    logVerbose('cleanUp', `consensus register collection was cleaned up`);
  }
}
